package org.vreo.devicehub.features.userAuthentication.presentation.register

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.userAuthentication.domain.User
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.userAuthentication.domain.UserAuthenticationRepository

/**
 * User registration view model.
 *
 * @param repository repository for accessing authentication APIs
 */
class UserRegistrationViewModel(
    private val repository: UserAuthenticationRepository
) : ViewModel() {
    private val _state = MutableStateFlow(UserRegistrationState())
    val state = _state.asStateFlow()

    // Email regex
    // - One or more characters (letters, numbers, underscores, dots, hyphens)
    // - Followed by an '@' symbol
    // - Followed by one or more characters (letters, numbers, hyphens)
    // - Followed by a dot (escaped as '\\.')
    // - Followed by 2 to 6 characters (the top-level domain, e.g., com, org, co.uk)
    private val emailRegex = Regex(
        "[a-zA-Z0-9+._%\\-]{1,256}" +
                "@" +
                "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,64}" +
                "(" +
                "\\." +
                "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,25}" +
                ")+"
    )

    // Phone regex
    private val phoneRegex = Regex("^[0-9+ ]*$")

    // Minimum password length
    private val minPasswordLength = 6

    /**
     * Handles user registration actions.
     *
     * @param action action to handle
     */
    fun onAction(action: UserRegistrationActions) {
        when (action) {
            is UserRegistrationActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is UserRegistrationActions.OnRegisterClicked -> {
                registerUser(action.callback)
            }

            is UserRegistrationActions.OnBackNavigationClicked -> {}

            is UserRegistrationActions.OnUpdateEmail -> {
                updateEmail(action.value)
            }

            is UserRegistrationActions.OnUpdateFirstName -> {
                _state.update {
                    it.copy(
                        firstName = action.value,
                        invalidFirstName = !(action.value.isNotBlank())
                    )
                }
            }

            is UserRegistrationActions.OnUpdateLastName -> {
                _state.update {
                    it.copy(
                        lastName = action.value,
                        invalidLastName = !(action.value.isNotBlank())
                    )
                }
            }

            is UserRegistrationActions.OnUpdateMobileNumber -> {
                updateMobileNumber(action.value)
            }

            is UserRegistrationActions.OnUpdatePassword -> {
                updatePassword(action.value)
            }

            is UserRegistrationActions.OnUpdateConfirmPassword -> {
                updateConfirmPassword(action.value)
            }

            is UserRegistrationActions.OnUpdateCompanyName -> {
                updateCompanyName(action.value)
            }

            is UserRegistrationActions.OnUpdateCompanyAddressStreet -> {
                _state.update {
                    it.copy(
                        companyAddressStreet = action.value
                    )
                }
            }

            is UserRegistrationActions.OnUpdateCompanyAddressCity -> {
                _state.update {
                    it.copy(
                        companyAddressCity = action.value
                    )
                }
            }

            is UserRegistrationActions.OnUpdateCompanyAddressPostalCode -> {
                _state.update {
                    it.copy(
                        companyAddressPostalCode = action.value
                    )
                }
            }

            is UserRegistrationActions.OnUpdateCompanyAddressCountry -> {
                _state.update {
                    it.copy(
                        companyAddressCountry = action.value
                    )
                }
            }
        }
    }

    /**
     * Verify and update email
     *
     * @param value value to update
     */
    private fun updateEmail(value: String) {
        val validEmail = emailRegex.matches(value)
        _state.update {
            it.copy(
                email = value,
                invalidEmail = !validEmail
            )
        }
    }

    /**
     * Update and validate mobile number
     *
     * @param value value to update
     */
    private fun updateMobileNumber(value: String) {
        if (phoneRegex.matches(value)) {
            _state.update {
                it.copy(
                    mobileNumber = value,
                )
            }
        }
    }

    /**
     * Update and validate company name
     *
     * @param value value to update
     */
    private fun updateCompanyName(value: String) {
        _state.update {
            it.copy(
                companyName = value,
                invalidCompanyName = (value.isBlank())
            )
        }
    }

    /**
     * Update and validate password
     *
     * @param value value to update
     */
    private fun updatePassword(value: String) {
        val validLength = (value.length >= minPasswordLength)
        val includesCapitalLetter = Regex(".*[A-Z].*")
        val includesSmallLetter = Regex(".*[a-z].*")
        val includesDigit = Regex(".*[0-9].*")

        val validPassword = (validLength &&
                includesCapitalLetter.matches(value) &&
                includesSmallLetter.matches(value) &&
                includesDigit.matches(value)
                )

        _state.update {
            it.copy(
                password = value,
                passwordMinimumLengthCheck = validLength,
                passwordUppercaseCheck = includesCapitalLetter.matches(value),
                passwordLowercaseCheck = includesSmallLetter.matches(value),
                passwordNumberCheck = includesDigit.matches(value),
                invalidPassword = !validPassword
            )
        }
    }

    /**
     * Update confirm password, validates if password and confirm password are the same
     *
     * @param value value to update
     */
    private fun updateConfirmPassword(value: String) {
        _state.update {
            it.copy(
                confirmPassword = value,
                invalidConfirmPassword = (state.value.password.compareTo(value) != 0)
            )
        }
    }

    /**
     * Register user
     *
     * @param callback callback to be called on successful registration
     */
    private fun registerUser(
        callback: ((userEmail: String, validUntil: Long) -> Unit)? = null
    ) = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        // Construct user details
        val user = try {
            if (!verifyUserDetails()) {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = DataError.Local.INVALID_ARGUMENT.toUiText()
                    )
                }
                return@launch
            }

            User(
                email = _state.value.email,
                password = _state.value.password,
                firstName = _state.value.firstName,
                lastName = _state.value.lastName,
                mobileNumber = _state.value.mobileNumber,
                companyName = _state.value.companyName,
                companyAddressStreet = _state.value.companyAddressStreet,
                companyAddressCity = _state.value.companyAddressCity,
                companyAddressPostalCode = _state.value.companyAddressPostalCode,
                companyAddressCountry = _state.value.companyAddressCountry,
            )
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
            return@launch
        }

        // Register user
        try {
            val res = repository.signUp(user)
            res
                .onSuccess { data ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null
                        )
                    }

                    callback?.invoke(
                        user.email,
                        data
                    )
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText()
                        )
                    }
                }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Verify user details, check if any required field is invalid
     *
     * @return true if all fields are valid,
     */
    private fun verifyUserDetails(): Boolean {

        // Check for empty fields
        val populatedFields = !(state.value.email.isBlank() ||
                state.value.password.isBlank() ||
                state.value.confirmPassword.isBlank() ||
                state.value.firstName.isBlank() ||
                state.value.lastName.isBlank() ||
                state.value.companyName.isBlank())

        if (!populatedFields) {
            _state.update {
                it.copy(
                    invalidEmail = state.value.email.isBlank(),
                    invalidPassword = state.value.password.isBlank(),
                    invalidConfirmPassword = state.value.confirmPassword.isBlank(),
                    invalidFirstName = state.value.firstName.isBlank(),
                    invalidLastName = state.value.lastName.isBlank(),
                    invalidCompanyName = state.value.companyName.isBlank()
                )
            }
        }

        // Check invalid fields
        val invalidFields = !(state.value.invalidEmail ||
                state.value.invalidPassword ||
                state.value.invalidConfirmPassword ||
                state.value.invalidFirstName ||
                state.value.invalidLastName ||
                state.value.invalidCompanyName)

        return invalidFields && populatedFields
    }
}